<script>
import axios from 'axios';
import ContentTreeNode from './ContentTreeNode.vue';
import ContentBranchCard from './ContentBranchCard.vue';
export default {
  name: 'ContentTree', components: { ContentTreeNode, ContentBranchCard },
  props: { courseId: [String, Number], academicYearId: [String, Number], branchNavigation: Boolean },
  data: () => ({
    groups: [], loading: true, error: '', request: null, expandedNodes: new Set(),
    // Set from /web/content-tree's own response (SubscriptionGate — see backend)
    // so an expired subscription is never confused with "genuinely no content".
    subscriptionExpired: false, subscribable: null,
  }),
  computed: {
    scope() { return { course_id: this.courseId || undefined, academic_year_id: this.academicYearId || undefined }; },
    requestedNodeId() { return this.branchNavigation ? this.$route.hash.match(/^#node-(\d+)$/)?.[1] : null; },
    rootBranches() {
      // The API's owner groups can share an ancestor. Show that root once,
      // retaining each owner's existing subtree when the branch is opened.
      const roots = new Map();
      for (const group of this.groups) for (const node of group.children) {
        if (!roots.has(node.id)) roots.set(node.id, node);
      }
      return [...roots.values()];
    },
    selectedRoot() {
      if (!this.requestedNodeId) return null;
      for (const group of this.groups) {
        const root = group.children.find(node => this.containsNode(node, this.requestedNodeId));
        if (root) return root;
      }
      return null;
    },
    visibleGroups() {
      if (!this.branchNavigation) return this.groups;
      if (!this.selectedRoot) return [];
      return this.groups.map(group => ({ ...group, children: group.children.filter(node => node.id === this.selectedRoot.id) }))
        .filter(group => group.children.length);
    },
    rootsUrl() { return { path: this.$route.path, query: this.$route.query, hash: '' }; },
  },
  watch: {
    scope: { immediate: true, handler() { this.load(); } },
    requestedNodeId() { this.revealNode(); },
    loading(value) { if (!value) this.revealNode(); },
  },
  methods: {
    setExpanded({ key, open }) {
      if (open) this.expandedNodes.add(key);
      else this.expandedNodes.delete(key);
    },
    containsNode(node, id) {
      return String(node.id) === id || node.children.some(child => this.containsNode(child, id));
    },
    async revealNode() {
      await this.$nextTick();
      if (this.loading || !this.selectedRoot) return;
      const target = this.$el.querySelector('[data-node-id="' + this.requestedNodeId + '"]');
      if (!target) return;
      for (let element = target; element && element !== this.$el; element = element.parentElement) {
        // Reveal only the hash target and its ancestors; leave siblings and descendants alone.
        if (element.tagName === 'DETAILS') this.setExpanded({ key: element.id, open: true });
      }
      await this.$nextTick();
      target.querySelector('summary')?.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start' });
    },
    async load() {
      this.request?.abort();
      const request = new AbortController(); this.request = request;
      this.loading = true; this.error = ''; this.subscriptionExpired = false; this.subscribable = null;
      try {
        const res = await axios.get('/web/content-tree', { params: this.scope, signal: request.signal });
        if (!request.signal.aborted) {
          this.groups = res.data.data;
          this.subscriptionExpired = !!res.data.subscription_expired;
          this.subscribable = res.data.subscribable || null;
        }
      } catch (e) {
        if (!request.signal.aborted) this.error = e.response?.data?.message || 'تعذر تحميل المحتوى. حاول مرة أخرى.';
      } finally { if (!request.signal.aborted) this.loading = false; }
    },
    goRenew() {
      this.$router.push({
        path: '/subscribe',
        query: {
          type: this.subscribable?.type || undefined,
          id: this.subscribable?.id || undefined,
          return: this.$route.fullPath,
        },
      });
    },
  },
  beforeUnmount() { this.request?.abort(); },
};
</script>
<template>
  <section class="content-tree" dir="rtl" aria-label="شجرة المحتوى">
    <p v-if="loading" role="status"><i class="pi pi-spin pi-spinner"></i> جاري تحميل المحتوى...</p>
    <div v-else-if="error" role="alert"><p>{{ error }}</p><button class="btn btn-outline-primary" @click="load">إعادة المحاولة</button></div>
    <template v-else>
      <div v-if="subscriptionExpired" class="subscription-expired" role="status">
        <div class="subscription-expired__icon" aria-hidden="true"><i class="pi pi-lock"></i></div>
        <h2 class="subscription-expired__title">انتهى اشتراكك 🔒</h2>
        <p class="subscription-expired__text">انتهى اشتراكك الحالي، لذلك لم يعد بإمكانك الوصول إلى محتوى الحصص.</p>
        <button type="button" class="subscription-expired__cta" @click="goRenew">
          <i class="pi pi-bolt" aria-hidden="true"></i> جدد اشتراكك
        </button>
      </div>
      <template v-else>
        <div v-if="branchNavigation && !requestedNodeId" class="branch-grid">
          <ContentBranchCard v-for="node in rootBranches" :key="node.id" :node="node" />
        </div>
        <template v-else>
          <nav v-if="branchNavigation" class="branch-navigation" aria-label="التنقل بين الفروع">
            <router-link :to="rootsUrl" class="branch-back"><i class="pi pi-arrow-right" aria-hidden="true"></i> جميع الفروع</router-link>
            <span v-if="selectedRoot">{{ selectedRoot.title }}</span>
          </nav>
          <p v-if="branchNavigation && !selectedRoot" role="status">هذا الفرع غير متاح لحسابك. اختر فرعاً من جميع الفروع.</p>
          <section v-for="group in visibleGroups" :key="group.key" class="tree-owner">
            <h2>{{ group.title }}</h2>
            <ContentTreeNode v-for="node in group.children" :key="node.id" :node="node" :id-prefix="group.key + '-'"
              :expanded-nodes="expandedNodes" @node-toggle="setExpanded" />
          </section>
        </template>
        <p v-if="!groups.length" role="status">لا يوجد محتوى متاح لحسابك حالياً.</p>
      </template>
    </template>
  </section>
</template>
<style scoped>
.content-tree { --tree-accent: #5b5fef; --tree-surface: #fff; --tree-text: #172033; min-width: 0; width: 100%; }
.tree-owner { margin-bottom: 1.5rem; }
.branch-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr)); gap: 1.25rem; }
.branch-navigation { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem; margin-bottom: 1.25rem; overflow-wrap: anywhere; }
.branch-back { display: inline-flex; align-items: center; gap: .5rem; padding: .65rem .85rem; border: 1px solid #94a3b844; border-radius: .65rem; text-decoration: none; color: var(--tree-accent); background: var(--tree-surface); }
.branch-back:focus-visible { outline: 2px solid var(--tree-accent); outline-offset: 2px; }
h2 { font-size: 1.3rem; margin-bottom: 1rem; }

.subscription-expired {
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: .75rem;
  padding: 3rem 1.25rem; max-width: 460px; margin: 0 auto;
}
.subscription-expired__icon {
  width: 76px; height: 76px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 2rem; background: rgba(239, 68, 68, .1); color: #ef4444;
}
.subscription-expired__title { font-size: 1.35rem; font-weight: 800; color: var(--tree-text); margin: 0; }
.subscription-expired__text { color: #6b7280; line-height: 1.8; margin: 0; }
.subscription-expired__cta {
  margin-top: .5rem; border: none; border-radius: 12px; padding: .9rem 1.85rem; font-weight: 800; font-size: 1rem;
  color: #fff; background: linear-gradient(90deg, var(--tree-accent), #7c4dff); cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem; min-height: 48px; width: 100%;
}
.subscription-expired__cta:focus-visible { outline: 2px solid var(--tree-accent); outline-offset: 2px; }
@media (min-width: 480px) { .subscription-expired__cta { width: auto; min-width: 220px; } }
</style>
